import React, { useEffect, useState } from 'react';
import { playersAPI, injuriesAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Heart, AlertTriangle, Activity, CheckCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getStatusColor, getStatusBgColor } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const injuryTypes = [
  'Sprain', 'Strain', 'Fracture', 'Contusion', 'Concussion',
  'Dislocation', 'Tendonitis', 'Muscle Tear', 'ACL Injury', 'Other'
];

const bodyParts = [
  'Head', 'Neck', 'Shoulder', 'Arm', 'Elbow', 'Wrist', 'Hand',
  'Back', 'Hip', 'Thigh', 'Knee', 'Ankle', 'Foot', 'Other'
];

export default function InjuriesPage() {
  const { user } = useAuth();
  const isTrainer = user?.role === 'trainer';
  const [players, setPlayers] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedInjury, setSelectedInjury] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    player_id: '',
    injury_type: '',
    body_part: '',
    severity: 'mild',
    description: '',
    treatment_plan: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [playersRes, injuriesRes] = await Promise.all([
        playersAPI.getAll(),
        injuriesAPI.getAll()
      ]);
      setPlayers(playersRes.data);
      setInjuries(injuriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      player_id: '',
      injury_type: '',
      body_part: '',
      severity: 'mild',
      description: '',
      treatment_plan: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await injuriesAPI.create(formData);
      toast.success('Injury logged');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log injury');
    }
  };

  const handleStatusChange = async (injuryId, newStatus) => {
    try {
      await injuriesAPI.update(injuryId, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      loadData();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateNotes = async (injuryId, notes) => {
    try {
      await injuriesAPI.update(injuryId, { notes });
      toast.success('Notes updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update notes');
    }
  };

  const openDetail = (injury) => {
    setSelectedInjury(injury);
    setDetailDialogOpen(true);
  };

  // Group injuries by status
  const newInjuries = injuries.filter(i => i.status === 'new');
  const rehabInjuries = injuries.filter(i => i.status === 'rehab');
  const clearedInjuries = injuries.filter(i => i.status === 'cleared');

  const InjuryCard = ({ injury }) => (
    <div 
      onClick={() => openDetail(injury)}
      className="injury-card cursor-pointer"
      data-testid={`injury-card-${injury.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-[#71717A]" />
          <span className="text-white font-medium text-sm">{injury.player_name}</span>
        </div>
        <span className={`text-xs px-2 py-0.5 border ${getStatusBgColor(injury.severity)} ${getStatusColor(injury.severity)}`}>
          {injury.severity.toUpperCase()}
        </span>
      </div>
      <p className="text-[#A1A1AA] text-sm">{injury.injury_type} - {injury.body_part}</p>
      <p className="text-xs text-[#71717A] mt-2">{formatDate(injury.injury_date)}</p>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="overline">Athletic Trainer</p>
            <h1 className="font-display text-4xl lg:text-5xl text-white mt-2">INJURIES</h1>
          </div>

          {isTrainer && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#FF3B30] hover:bg-[#D32F2F] text-white btn-press"
                  data-testid="log-injury-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Log Injury
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">LOG INJURY</DialogTitle>
                <DialogDescription className="text-[#71717A]">
                  Record a new injury for a team member
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-[#A1A1AA]">Player</Label>
                  <Select
                    value={formData.player_id}
                    onValueChange={(value) => setFormData({...formData, player_id: value})}
                  >
                    <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white" data-testid="injury-player-select">
                      <SelectValue placeholder="Select player" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      {players.map(player => (
                        <SelectItem key={player.id} value={player.id} className="text-white hover:bg-[#27272A]">
                          #{player.jersey_number} - {player.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#A1A1AA]">Injury Type</Label>
                    <Select
                      value={formData.injury_type}
                      onValueChange={(value) => setFormData({...formData, injury_type: value})}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white" data-testid="injury-type-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        {injuryTypes.map(type => (
                          <SelectItem key={type} value={type} className="text-white hover:bg-[#27272A]">
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Body Part</Label>
                    <Select
                      value={formData.body_part}
                      onValueChange={(value) => setFormData({...formData, body_part: value})}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white" data-testid="injury-body-part-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        {bodyParts.map(part => (
                          <SelectItem key={part} value={part} className="text-white hover:bg-[#27272A]">
                            {part}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Severity</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {['mild', 'moderate', 'severe'].map(level => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setFormData({...formData, severity: level})}
                        className={`p-3 border text-sm font-medium transition-all ${
                          formData.severity === level
                            ? getStatusBgColor(level) + ' ' + getStatusColor(level)
                            : 'border-[#27272A] text-[#71717A] hover:border-[#3F3F46]'
                        }`}
                        data-testid={`severity-${level}-btn`}
                      >
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                    placeholder="Describe how the injury occurred..."
                    rows={3}
                    data-testid="injury-description-input"
                  />
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Treatment Plan</Label>
                  <Textarea
                    value={formData.treatment_plan}
                    onChange={(e) => setFormData({...formData, treatment_plan: e.target.value})}
                    className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                    placeholder="Initial treatment plan..."
                    rows={2}
                    data-testid="injury-treatment-input"
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white"
                  disabled={!formData.player_id || !formData.injury_type || !formData.body_part}
                  data-testid="save-injury-btn"
                >
                  Log Injury
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-[#121212] border-[#27272A]" data-testid="stat-new">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FF3B30]/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-[#FF3B30]" />
              </div>
              <div>
                <p className="font-display text-3xl text-[#FF3B30]">{newInjuries.length}</p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider">New</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-[#27272A]" data-testid="stat-rehab">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FFD60A]/10 flex items-center justify-center">
                <Activity className="w-6 h-6 text-[#FFD60A]" />
              </div>
              <div>
                <p className="font-display text-3xl text-[#FFD60A]">{rehabInjuries.length}</p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider">In Rehab</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-[#27272A]" data-testid="stat-cleared">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#32D74B]/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#32D74B]" />
              </div>
              <div>
                <p className="font-display text-3xl text-[#32D74B]">{clearedInjuries.length}</p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider">Cleared</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Kanban Board */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-[#71717A]">Loading...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="injury-board">
            {/* New Injuries */}
            <div className="injury-column p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 bg-[#FF3B30]"></div>
                <h3 className="font-display text-lg text-white">NEW INJURIES</h3>
                <span className="ml-auto text-xs text-[#71717A] bg-[#27272A] px-2 py-1">{newInjuries.length}</span>
              </div>
              <div className="space-y-2">
                {newInjuries.map(injury => <InjuryCard key={injury.id} injury={injury} />)}
                {newInjuries.length === 0 && (
                  <p className="text-center text-[#71717A] py-8 text-sm">No new injuries</p>
                )}
              </div>
            </div>

            {/* In Rehab */}
            <div className="injury-column p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 bg-[#FFD60A]"></div>
                <h3 className="font-display text-lg text-white">IN REHAB</h3>
                <span className="ml-auto text-xs text-[#71717A] bg-[#27272A] px-2 py-1">{rehabInjuries.length}</span>
              </div>
              <div className="space-y-2">
                {rehabInjuries.map(injury => <InjuryCard key={injury.id} injury={injury} />)}
                {rehabInjuries.length === 0 && (
                  <p className="text-center text-[#71717A] py-8 text-sm">No players in rehab</p>
                )}
              </div>
            </div>

            {/* Cleared */}
            <div className="injury-column p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 bg-[#32D74B]"></div>
                <h3 className="font-display text-lg text-white">CLEARED</h3>
                <span className="ml-auto text-xs text-[#71717A] bg-[#27272A] px-2 py-1">{clearedInjuries.length}</span>
              </div>
              <div className="space-y-2">
                {clearedInjuries.slice(0, 5).map(injury => <InjuryCard key={injury.id} injury={injury} />)}
                {clearedInjuries.length === 0 && (
                  <p className="text-center text-[#71717A] py-8 text-sm">No cleared injuries</p>
                )}
                {clearedInjuries.length > 5 && (
                  <p className="text-center text-[#71717A] text-sm">+{clearedInjuries.length - 5} more</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Injury Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
            {selectedInjury && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">INJURY DETAILS</DialogTitle>
                  <DialogDescription className="text-[#71717A]">
                    View and manage injury information
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium text-lg">{selectedInjury.player_name}</p>
                      <p className="text-[#71717A] text-sm">{formatDate(selectedInjury.injury_date)}</p>
                    </div>
                    <span className={`px-3 py-1 text-sm font-medium border ${getStatusBgColor(selectedInjury.status)} ${getStatusColor(selectedInjury.status)}`}>
                      {selectedInjury.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-4 bg-[#0A0A0A] border border-[#27272A]">
                    <div>
                      <p className="text-xs text-[#71717A] uppercase tracking-wider">Type</p>
                      <p className="text-white mt-1">{selectedInjury.injury_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#71717A] uppercase tracking-wider">Body Part</p>
                      <p className="text-white mt-1">{selectedInjury.body_part}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#71717A] uppercase tracking-wider">Severity</p>
                      <p className={`mt-1 ${getStatusColor(selectedInjury.severity)}`}>
                        {selectedInjury.severity.charAt(0).toUpperCase() + selectedInjury.severity.slice(1)}
                      </p>
                    </div>
                  </div>

                  {selectedInjury.description && (
                    <div>
                      <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Description</p>
                      <p className="text-[#A1A1AA] text-sm">{selectedInjury.description}</p>
                    </div>
                  )}

                  {selectedInjury.treatment_plan && (
                    <div>
                      <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Treatment Plan</p>
                      <p className="text-[#A1A1AA] text-sm">{selectedInjury.treatment_plan}</p>
                    </div>
                  )}

                  {selectedInjury.notes && (
                    <div>
                      <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Notes</p>
                      <p className="text-[#A1A1AA] text-sm">{selectedInjury.notes}</p>
                    </div>
                  )}

                  {/* Status Actions - Only visible to Trainer */}
                  {isTrainer && (
                    <div className="pt-4 border-t border-[#27272A]">
                      <p className="text-xs text-[#71717A] uppercase tracking-wider mb-3">Update Status</p>
                      <div className="flex gap-2">
                        {selectedInjury.status !== 'new' && (
                          <Button
                            onClick={() => handleStatusChange(selectedInjury.id, 'new')}
                            className="flex-1 bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
                            data-testid="status-new-btn"
                          >
                            New
                          </Button>
                        )}
                        {selectedInjury.status !== 'rehab' && (
                          <Button
                            onClick={() => handleStatusChange(selectedInjury.id, 'rehab')}
                            className="flex-1 bg-[#FFD60A]/10 text-[#FFD60A] border border-[#FFD60A]/30 hover:bg-[#FFD60A]/20"
                            data-testid="status-rehab-btn"
                          >
                            In Rehab
                          </Button>
                        )}
                        {selectedInjury.status !== 'cleared' && (
                          <Button
                            onClick={() => handleStatusChange(selectedInjury.id, 'cleared')}
                            className="flex-1 bg-[#32D74B]/10 text-[#32D74B] border border-[#32D74B]/30 hover:bg-[#32D74B]/20"
                            data-testid="status-cleared-btn"
                          >
                            Cleared
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
